import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import reportService from "../../services/reportService";
import Button from "../../components/Button";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import "../../assets/fonts/NotoSansTamil-Regular-normal";
import html2pdf from "html2pdf.js";

type LoanDetail = {
  loan_code: string;
  loan_created_date: string;
  borrower_id: number;
  registration_number: number;
  loan_amount: string;
  repayment_frequency: string;
  tenure_months: number;
  emi_amount: string;
  total_amount: string;
  start_date: string;
  end_date: string;
  status: string;
  last_name: string;
  first_name: string;
  name: string;
};
type Transaction = {
  loan_code: string;
  amount_paid: string;
  payment_mode: string;
  created_at: string;
  status: string;
  loan: any;
  due_label: string;
};

type ReportResponse = {
  from_date: string;
  to_date: string;
  total_loans: number;
  total_loan_amount: number;
  total_interest_amount: number;
  total_received_amount: string | number;
  total_outstanding: number;
  loan_details: LoanDetail[];
  transactions: Transaction[];
};

function formatCurrency(value: number | string) {
  const n = typeof value === "string" ? Number(value) : value;
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString(undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
}

function isoDate(d: Date) {
  return d.toISOString().slice(0, 10);
}

function useAnimatedNumber(target: number, duration = 600) {
  const [value, setValue] = useState(0);
  const ref = useRef<{ start: number; from: number; raf?: number } | null>(
    null,
  );

  useEffect(() => {
    const start = performance.now();
    const from = value;
    const diff = target - from;
    ref.current = { start, from };

    function step(now: number) {
      if (!ref.current) return;
      const t = Math.min(1, (now - ref.current.start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out
      setValue(Math.round(ref.current.from + diff * eased));
      if (t < 1) ref.current!.raf = requestAnimationFrame(step);
    }

    ref.current.raf = requestAnimationFrame(step);
    return () => {
      if (ref.current?.raf) cancelAnimationFrame(ref.current.raf);
      ref.current = null;
    };
  }, [target]);

  return value;
}

function SkeletonCard() {
  return (
    <div
      role="status"
      aria-hidden="true"
      className="p-4 rounded-xl shadow-sm bg-gradient-to-br from-white to-gray-50 border border-gray-100"
    >
      {/* Title */}
      <div className="h-3 w-28 bg-gray-200 rounded mb-3 animate-pulse" />
      {/* Big number */}
      <div className="h-9 w-36 bg-gray-200 rounded font-semibold text-2xl leading-9 animate-pulse" />
    </div>
  );
}

function StatCard({
  title,
  value,
  prefix = "",
}: {
  title: string;
  value: string | number;
  prefix?: string;
}) {
  return (
    <div className="p-4 rounded-xl shadow-sm bg-gradient-to-br from-white to-gray-50 border border-gray-100 transform transition-all duration-200 hover:-translate-y-1 hover:shadow-md">
      <div className="text-xs text-gray-500">{title}</div>
      <div className="text-2xl font-semibold mt-2">
        {prefix}
        {value}
      </div>
    </div>
  );
}

const formatAmount = (value: string | number) => {
  if (!value) return "0.00";
  return value;
};

function formatDateTime(isoDate: string) {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

function getCustomerName(loan: LoanDetail): string {
  if (loan.name) return loan.name;
  const firstName = loan.first_name || "";
  const lastName = loan.last_name || "";
  return `${firstName} ${lastName}`.trim();
}
function drawFooter(doc: jsPDF) {
  const pageHeight = doc.internal.pageSize.height;
  const pageWidth = doc.internal.pageSize.width;

  doc.setFontSize(9);
  doc.setTextColor(120);

  doc.text("Sri Ambigai Finance", pageWidth / 2, pageHeight - 18, {
    align: "center",
  });

  doc.text(
    "Chinthamathar Pallivasal Street, Railway Feeder Road",
    pageWidth / 2,
    pageHeight - 13,
    { align: "center" },
  );

  doc.text("Kadayanallur - 627751", pageWidth / 2, pageHeight - 8, {
    align: "center",
  });
  doc.text(
    "Gpay : 74185 33995 , 72003 08141 ,Office: 78453 20041 , 93457 91309",
    pageWidth / 2,
    pageHeight - 4,
    { align: "center" },
  );
}

const generateReportHTML = (report: ReportResponse) => {
  return `
  <div style="
    font-family: 'Noto Sans Tamil', sans-serif;
    padding: 20px;
    font-size: 12px;
    color:#222;
  ">

    <!-- HEADER -->
    <div style="text-align:center; margin-bottom:10px;">
      <h2 style="margin:0;">Sri Ambigai Finance</h2>
      <div style="font-size:11px; color:#555;">
        Loan Report
      </div>
      <div style="font-size:10px; color:#888;">
        Generated on: ${formatDateTime(new Date().toISOString().slice(0, 10))}
      </div>
    </div>

    <!-- SUMMARY -->
    <table style="
      width:100%;
      border-collapse:collapse;
      margin-top:10px;
      border:1px solid #ccc;
    ">
      <tr style="background:#2c3e50; color:white;">
        <th style="padding:8px; text-align:left;">Summary</th>
        <th style="padding:8px; text-align:left;">Value</th>
      </tr>
      ${[
        ["From Date", formatDateTime(report.from_date)],
        ["To Date", formatDateTime(report.to_date)],
        ["Total Loans", report.total_loans],
        ["Total Loan Amount", report.total_loan_amount],
        ["Total Outstanding", report.total_outstanding],
        ["Total Received", report.total_received_amount],
      ]
        .map(
          ([k, v], i) => `
        <tr style="background:${i % 2 === 0 ? "#f9f9f9" : "#ffffff"};">
          <td style="padding:6px;">${k}</td>
          <td style="padding:6px;">${v}</td>
        </tr>
      `,
        )
        .join("")}
    </table>

    <!-- LOAN DETAILS -->
    <h3 style="margin-top:20px; margin-bottom:5px;">Loan Details</h3>

    <table style="
      width:100%;
      border-collapse:collapse;
      table-layout:fixed;
      border:1px solid #ccc;
    ">
      <tr style="background:#2c3e50; color:white;">
        <th style="width:10%; padding:6px;">Code</th>
        <th style="width:10%; padding:6px;">Date</th>
        <th style="width:20%; padding:6px;">Customer</th>
        <th style="width:15%; padding:6px;">Reg No</th>
        <th style="width:10%; padding:6px;">Loan</th>
        <th style="width:10%; padding:6px;">EMI</th>
        <th style="width:8%; padding:6px;">Tenure</th>
        <th style="width:10%; padding:6px;">Total</th>
        <th style="width:7%; padding:6px;">Status</th>
      </tr>

      ${report.loan_details
        .map(
          (loan, i) => `
        <tr style="background:${i % 2 === 0 ? "#f9f9f9" : "#ffffff"};">
          <td style="padding:6px;">${loan.loan_code}</td>
          <td style="padding:6px;">${formatDateTime(loan.loan_created_date)}</td>
          <td style="padding:6px;">${getCustomerName(loan)}</td>
          <td style="padding:6px;">${loan.registration_number}</td>
          <td style="padding:6px; text-align:right;">${loan.loan_amount}</td>
          <td style="padding:6px; text-align:right;">${loan.emi_amount}</td>
          <td style="padding:6px;">${loan.tenure_months} M</td>
          <td style="padding:6px; text-align:right;">${loan.total_amount}</td>
          <td style="padding:6px;">${loan.status}</td>
        </tr>
      `,
        )
        .join("")}
    </table>

    <!-- CASH -->
    <h3 style="margin-top:20px; margin-bottom:5px;">Cash Transactions</h3>

    <table style="
      width:100%;
      border-collapse:collapse;
      border:1px solid #ccc;
    ">
      <tr style="background:#2c3e50; color:white;">
        <th style="padding:6px;">Loan</th>
        <th style="padding:6px;">Due</th>
        <th style="padding:6px;">Amount</th>
        <th style="padding:6px;">Mode</th>
        <th style="padding:6px;">Date</th>
        <th style="padding:6px;">Status</th>
      </tr>

      ${report.transactions
        .filter((t) => t.payment_mode?.toLowerCase() === "cash")
        .map(
          (t, i) => `
        <tr style="background:${i % 2 === 0 ? "#f9f9f9" : "#ffffff"};">
          <td style="padding:6px;">${t.loan?.loan_code}</td>
          <td style="padding:6px; text-align:center;">${t.due_label}</td>
          <td style="padding:6px; text-align:center;">${t.amount_paid}</td>
          <td style="padding:6px; text-align:center;">${t.payment_mode}</td>
          <td style="padding:6px; text-align:center;">${formatDateTime(t.created_at)}</td>
          <td style="padding:6px; text-align:center;">${t.status}</td>
        </tr>
      `,
        )
        .join("")}
    </table>

    <!-- UPI -->
    <h3 style="margin-top:20px; margin-bottom:5px;">UPI Transactions</h3>

    <table style="
      width:100%;
      border-collapse:collapse;
      border:1px solid #ccc;
    ">
      <tr style="background:#2c3e50; color:white;">
        <th style="padding:6px;">Loan</th>
        <th style="padding:6px;">Due</th>
        <th style="padding:6px;">Amount</th>
        <th style="padding:6px;">Mode</th>
        <th style="padding:6px;">Date</th>
        <th style="padding:6px;">Status</th>
      </tr>

      ${report.transactions
        .filter((t) => t.payment_mode?.toLowerCase() === "upi")
        .map(
          (t, i) => `
        <tr style="background:${i % 2 === 0 ? "#f9f9f9" : "#ffffff"};">
          <td style="padding:6px;">${t.loan?.loan_code}</td>
          <td style="padding:6px;text-align:center;">${t.due_label}</td>
          <td style="padding:6px;text-align:center;">${t.amount_paid}</td>
          <td style="padding:6px;text-align:center;">${t.payment_mode}</td>
          <td style="padding:6px;text-align:center;">${formatDateTime(t.created_at)}</td>
          <td style="padding:6px;text-align:center;">${t.status}</td>
        </tr>
      `,
        )
        .join("")}
    </table>

    <!-- FOOTER -->
    <div style="
      text-align:center;
      margin-top:25px;
      font-size:10px;
      color:#666;
      border-top:1px solid #ddd;
      padding-top:10px;
    ">
      Sri Ambigai Finance <br/>
      Chinthamathar Pallivasal Street, Railway Feeder Road <br/>
      Kadayanallur - 627751 <br/>
      Gpay: 74185 33995 , 72003 08141
    </div>

  </div>
  `;
};

function exportReportPdf(report: ReportResponse) {
  const element = document.createElement("div");
  element.innerHTML = generateReportHTML(report);

  html2pdf()
    .set({
      margin: 5,
      filename: `loan_report_${report.from_date}_to_${report.to_date}.pdf`,
      image: { type: "jpeg", quality: 1 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    })
    .from(element)
    .save();
}

function exportReportExcel(report: ReportResponse) {
  const summarySheet = XLSX.utils.aoa_to_sheet([
    ["Loan Report Summary"],
    [],
    ["From Date", report.from_date],
    ["To Date", report.to_date],
    ["Total Loans", report.total_loans],
    ["Total Loan Amount", report.total_loan_amount],
    ["Total Received", report.total_received_amount],
    ["Total Outstanding", report.total_outstanding],
  ]);

  const loanTable = [
    [
      "Loan Code",
      "Created Date",
      "Customer Name",
      "Vehicle ID",
      "Loan Amount",
      "EMI Amount",
      "Tenure (Months)",
      "Total Amount",
      "Status",
    ],
    ...report.loan_details.map((loan) => [
      loan.loan_code,
      loan.loan_created_date,
      getCustomerName(loan),
      loan.registration_number,
      loan.loan_amount,
      loan.emi_amount,
      loan.tenure_months,
      loan.total_amount,
      loan.status,
    ]),
  ];

  const loanSheet = XLSX.utils.aoa_to_sheet(loanTable);

  const cashTransactions = report.transactions.filter(
    (t) => t?.payment_mode?.toLowerCase() === "cash",
  );

  const upiTransactions = report.transactions.filter(
    (t) => t?.payment_mode?.toLowerCase() === "upi",
  );

  const cashTransactionTable = [
    ["Cash Transactions"],
    [],
    ["Loan Code", "Due No", "Amount Paid", "Payment Mode", "Date", "Status"],
    ...cashTransactions.map((t) => [
      t?.loan?.loan_code ?? "",
      t?.due_label ?? "",
      t?.amount_paid ?? "",
      t?.payment_mode ?? "",
      t?.created_at ?? "",
      t?.status ?? "",
    ]),
  ];

  const cashSheet = XLSX.utils.aoa_to_sheet(cashTransactionTable);

  const upiTransactionTable = [
    ["UPI Transactions"],
    [],
    ["Loan Code", "Due No", "Amount Paid", "Payment Mode", "Date", "Status"],
    ...upiTransactions.map((t) => [
      t?.loan?.loan_code ?? "",
      t?.due_label ?? "",
      t?.amount_paid ?? "",
      t?.payment_mode ?? "",
      t?.created_at ?? "",
      t?.status ?? "",
    ]),
  ];

  const upiSheet = XLSX.utils.aoa_to_sheet(upiTransactionTable);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
  XLSX.utils.book_append_sheet(workbook, loanSheet, "Loan Details");
  XLSX.utils.book_append_sheet(workbook, cashSheet, "Cash Transactions");
  XLSX.utils.book_append_sheet(workbook, upiSheet, "UPI Transactions");

  XLSX.writeFile(
    workbook,
    `report_${report.from_date}_to_${report.to_date}.xlsx`,
  );
}

export default function ReportPage() {
  const today = new Date();
  const defaultTo = new Date(today);
  defaultTo.setDate(defaultTo.getDate() - 0);
  const defaultFrom = new Date(today);
  defaultFrom.setDate(defaultFrom.getDate() - 0);

  const [fromDate, setFromDate] = useState<string>(isoDate(defaultFrom));
  const [toDate, setToDate] = useState<string>(isoDate(defaultTo));

  const [report, setReport] = useState<ReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const mounted = useRef(true);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const invalidRange = useMemo(
    () => new Date(fromDate) > new Date(toDate),
    [fromDate, toDate],
  );

  async function fetchReport() {
    setError(null);
    if (invalidRange) {
      setError("From date must be before or equal to To date.");
      return;
    }

    // Show skeleton immediately
    setLoading(true);
    // Hide previous report while fetching
    setVisible(false);

    try {
      const data = await reportService.fetchReport(fromDate, toDate);
      if (!mounted.current) return;

      setReport({
        from_date: data.from_date,
        to_date: data.to_date,
        total_loans: data.total_loans,
        total_loan_amount: data.total_loan_amount,
        total_interest_amount: data.total_interest_amount,
        total_received_amount: data.total_received_amount,
        total_outstanding: data.total_outstanding,
        loan_details: data.loan_details ?? [],
        transactions: data.transactions ?? [],
      });

      timeoutRef.current = window.setTimeout(() => {
        if (!mounted.current) return;
        setVisible(true);
      }, 120);
    } catch (err: any) {
      if (!mounted.current) return;
      setReport(null);
      setError(err?.message ?? "Failed to fetch report");
    } finally {
      if (!mounted.current) return;
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchReport();
  }, []);

  function downloadCsv() {
    if (!report) return;
    const headers = [
      "from_date",
      "to_date",
      "total_loans",
      "total_loan_amount",
      "total_interest_amount",
      // "total_received_amount",
      // "total_outstanding",
    ];
    const row = [
      report.from_date,
      report.to_date,
      String(report.total_loans),
      String(report.total_loan_amount),
      String(report.total_interest_amount),
      // String(report.total_received_amount),
      // String(report.total_outstanding),
    ];
    const csv = `${headers.join(",")}\n${row.map((r) => `"${String(r).replace(/"/g, '""')}"`).join(",")}\n`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `report_${report.from_date}_to_${report.to_date}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function setPreset(days: number) {
    const to = new Date();
    to.setDate(to.getDate() - 1);
    const from = new Date(to);
    from.setDate(from.getDate() - (days - 1));
    setFromDate(isoDate(from));
    setToDate(isoDate(to));
  }

  const animLoans = useAnimatedNumber(report?.total_loans ?? 0);
  const animLoanAmount = useAnimatedNumber(
    Number(report?.total_loan_amount ?? 0),
  );
  const animInterest = useAnimatedNumber(
    Number(report?.total_interest_amount ?? 0),
  );
  const animReceived = useAnimatedNumber(
    Number(report?.total_received_amount ?? 0),
  );
  const animOutstanding = useAnimatedNumber(
    Number(report?.total_outstanding ?? 0),
  );

  const todayISO = isoDate(new Date());

  return (
    <div className="pt-3 pb-10">
      <div className="mx-auto ">
        <div className="rounded-2xl shadow p-6 bg-gradient-to-br from-white to-slate-50 ">
          <div className="flex items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-semibold">Reports</h1>
              <p className="text-sm text-gray-500 mt-1">
                Summary for selected date range
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label className="flex flex-col text-sm">
                <span className="text-xs text-gray-600 mb-1">From</span>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="px-3 py-2 border rounded-md w-full"
                  max={toDate}
                />
              </label>
            </div>

            <div>
              <label className="flex flex-col text-sm">
                <span className="text-xs text-gray-600 mb-1">To</span>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="px-3 py-2 border rounded-md w-full"
                  min={fromDate}
                  max={todayISO}
                />
              </label>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setFromDate(isoDate(defaultFrom));
                  setToDate(isoDate(defaultTo));
                  fetchReport();
                }}
                className="px-3 py-2 rounded-md bg-gray-100 hover:bg-gray-200"
              >
                Reset
              </button>
              <Button onClick={fetchReport} disabled={loading}>
                {loading ? (
                  <Icon
                    icon="mdi:loading"
                    className="animate-spin"
                    width={16}
                  />
                ) : (
                  <Icon icon="mdi:chart-line" width={16} />
                )}{" "}
                Apply
              </Button>
            </div>
          </div>

          <div className="mt-4">
            {invalidRange && (
              <div className="text-sm text-red-500">
                Invalid range — start date must be before or equal to end date.
              </div>
            )}
            {error && !invalidRange && (
              <div className="text-sm text-red-500">{error}</div>
            )}
          </div>

          {/* ---------- SKELETON: ---------- */}
          {loading && (
            <div className="space-y-6">
              {/* Header skeleton (Date range + buttons) */}
              <div className="animate-pulse    flex flex-col md:flex-row md:items-center md:justify-between">
                {/* Left side (Date range text) */}
                <div>
                  <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                  <div className="h-5 bg-gray-300 rounded w-48"></div>
                </div>

                {/* Right side (Buttons) */}
                <div className="flex gap-3 mt-4 md:mt-0">
                  {/* <div className="h-15 w-28 border border-gray-300 rounded-md bg-gray-200"></div> */}
                  {/* <div className="h-15 w-20 border border-gray-300 rounded-md bg-gray-200"></div> */}
                </div>
              </div>

              {/* Stat cards skeleton grid */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4">
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
              </div>
            </div>
          )}

          {/* ---------- FINAL REPORT CONTENT:---------- */}
          {!loading && (
            <div
              className={`mt-6 transition-opacity duration-300 ${visible ? "opacity-100" : "opacity-0"}`}
            >
              {!report && !error && (
                <div className="text-sm text-gray-600">
                  No report data. Adjust dates and click Generate.
                </div>
              )}

              {report && (
                <>
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <div className="text-xs text-gray-500">Date range</div>
                      <div className="text-base font-medium">
                        {formatDateTime(report.from_date)} →{" "}
                        {formatDateTime(report.to_date)}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      {/* <button onClick={downloadCsv} className="px-3 py-2 rounded-md bg-white border">
                                                <Icon icon="mdi:download" width={16} /> Export CSV
                                            </button> */}
                      {/* <button onClick={() => window.print()} className="px-3 py-2 rounded-md bg-white border">
                                                <Icon icon="mdi:printer" width={16} /> Print
                                            </button> */}
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <StatCard title="Total loans" value={animLoans} />
                    <StatCard
                      title="Total loan amount"
                      value={`₹ ${formatCurrency(animLoanAmount)}`}
                    />
                    {/* <StatCard title="Total interest amount" value={`₹ ${formatCurrency(animInterest)}`} /> */}
                    <StatCard
                      title="Total received"
                      value={`₹ ${formatCurrency(animReceived)}`}
                    />
                    <StatCard
                      title="Total outstanding"
                      value={`₹ ${formatCurrency(animOutstanding)}`}
                    />
                  </div>
                  <div className="flex justify-end gap-2 mt-4">
                    <Button
                      onClick={() => exportReportPdf(report)}
                      variant="secondary"
                      // className="px-3 py-2 rounded-md bg-white border hover:bg-gray-50 flex items-center gap-1"
                    >
                      <Icon icon="mdi:file-pdf-box" width={16} />
                      Download PDF
                    </Button>

                    <Button
                      onClick={() => exportReportExcel(report)}
                      variant="secondary"
                      // className="px-3 py-2 rounded-md bg-white border hover:bg-gray-50 flex items-center gap-1"
                    >
                      <Icon icon="mdi:file-excel" width={16} />
                      Export Excel
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
